import { useCallback, useEffect, useState } from 'react';
import { AiManagementUpdate, AiManagementUpdateComment } from 'services/ai_management_updates';
import {
  createUpdate,
  createUpdateNote,
  CreateUpdateInput,
  listUpdates,
} from 'services/actions/ai_management_updates';

const PER_PAGE = 100;

export function useManualCardUpdates(cardId: number) {
  const [updates, setUpdates] = useState<AiManagementUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);

    listUpdates({ card_id: cardId, source: 'manual', per_page: PER_PAGE })
      .then((response) => {
        if (!active) return;
        const list = response?.data?.updates;
        if (!Array.isArray(list)) {
          setError(true);
          return;
        }
        setUpdates(list);
      })
      .catch((err) => {
        console.error(`Erro ao listar as atualizações do card ${cardId}:`, err);
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [cardId]);

  const create = useCallback(
    (input: Omit<CreateUpdateInput, 'cardId'>) =>
      createUpdate({ ...input, cardId }).then((response) => {
        const created = response?.data?.update;
        if (!created) throw new Error('Resposta sem atualização');
        setUpdates((current) => [created, ...current]);
        return created;
      }),
    [cardId],
  );

  const addNote = useCallback(
    (updateId: number, text: string) =>
      createUpdateNote(updateId, text).then((response) => {
        const comment: AiManagementUpdateComment | undefined = response?.data?.comment;
        if (!comment) throw new Error('Resposta sem anotação');
        setUpdates((current) =>
          current.map((update) =>
            update.id === updateId
              ? { ...update, comments: [...(update.comments ?? []), comment] }
              : update,
          ),
        );
        return comment;
      }),
    [],
  );

  return { updates, loading, error, create, addNote };
}
